/**
 * AppDownloadPrompts.jsx (iter247, 2026-02)
 *
 * 4-touchpoint system that pushes the native app download to web users:
 *
 *   1. Full-screen modal on first page after signup (dismissible after 3s)
 *   2. Sticky top banner on every page (dismissible for the current session)
 *   3. Inline popup every 3 navigations
 *   4. Bottom toast after 5 minutes of continuous use
 *
 * The user is considered "converted to app" once they click any download
 * button → we save `localStorage.app_download_clicked = 1` and SILENCE
 * every prompt forever. Clearing localStorage brings them back.
 *
 * Design: dark/premium, 3 feature bullets (push, offline, speed).
 *
 * Why this file exists in isolation
 * ---------------------------------
 * All 4 touchpoints share the same OS detection + download URLs + "has
 * already downloaded" flag + Pixel tracking on click. Co-locating them
 * keeps the invariants in one place.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Download, Bell, Zap, WifiOff } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { trackPixelEvent } from '../lib/metaPixel';
import { useAuth } from '../contexts/AuthContext';

// ---------------------------------------------------------------------------
// OS detection + store URLs (user-provided, iter247)
// ---------------------------------------------------------------------------
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=it.rvincente.app.twa';
const APP_STORE_URL  = 'https://apps.apple.com/app/rvincente-pronostici-calcio/id6760314785';

export function getStoreUrl() {
  if (typeof navigator === 'undefined') return PLAY_STORE_URL;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return APP_STORE_URL;
  return PLAY_STORE_URL;
}

// ---------------------------------------------------------------------------
// Shared state helpers
// ---------------------------------------------------------------------------
const LS_DOWNLOADED = 'app_download_clicked';
const SS_MODAL_SHOWN = 'app_dl_modal_shown';
const SS_BANNER_DISMISSED = 'app_dl_banner_dismissed';
const SS_POPUP_LAST_PAGE = 'app_dl_popup_last_page';
const SS_PAGES_COUNT = 'app_dl_pages_count';
const SS_TOAST_SHOWN = 'app_dl_toast_shown';

export function hasUserDownloadedApp() {
  try { return localStorage.getItem(LS_DOWNLOADED) === '1'; } catch { return false; }
}

function markUserDownloadedApp() {
  try { localStorage.setItem(LS_DOWNLOADED, '1'); } catch {}
}

/** Running inside a native app (Capacitor iOS/Android) or standalone PWA:
 *  hide every download prompt. */
function isRunningInApp() {
  if (typeof window === 'undefined') return false;
  // iter254 — Primary check: Capacitor's own API. This is the only
  // reliable way to detect iOS Capacitor WKWebView (which has NO 'wv'
  // in UA and does NOT always set display-mode:standalone, so the
  // old heuristics missed it → users got stuck in a loop where the
  // native app kept asking them to download the native app). Also
  // catches Android Capacitor builds.
  try {
    if (Capacitor && typeof Capacitor.isNativePlatform === 'function' && Capacitor.isNativePlatform()) {
      return true;
    }
  } catch { /* fall through to UA heuristics */ }
  // Fallbacks for TWA / PWA installed to home screen
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari added to home screen
  if (window.navigator && window.navigator.standalone === true) return true;
  const ua = navigator.userAgent || '';
  // Trusted Web Activity hint: wv tag in UA + Android
  if (/wv/i.test(ua) && /Android/i.test(ua)) return true;
  return false;
}

/**
 * Called from the shared "Scarica" button in every touchpoint. Marks the
 * user as converted, fires a tracking event, and opens the correct store.
 */
function handleDownloadClick(source) {
  markUserDownloadedApp();
  try { trackPixelEvent('Lead', `dl_${source}_${Date.now()}`, { content_name: 'app_download', source }); } catch {}
  const url = getStoreUrl();
  // Open in new tab so the user doesn't lose their session
  window.open(url, '_blank', 'noopener,noreferrer');
}


// ---------------------------------------------------------------------------
// Shared visual blocks
// ---------------------------------------------------------------------------
const FeatureBullets = () => (
  <div className="space-y-3 my-5">
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl bg-[#F5A623]/20 flex items-center justify-center flex-shrink-0">
        <Bell className="w-4 h-4 text-[#F5A623]" />
      </div>
      <div>
        <p className="text-white font-bold text-sm">Notifiche push</p>
        <p className="text-gray-500 text-xs">Analisi VIP in anteprima, prima di tutti</p>
      </div>
    </div>
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl bg-[#F5A623]/20 flex items-center justify-center flex-shrink-0">
        <Zap className="w-4 h-4 text-[#F5A623]" />
      </div>
      <div>
        <p className="text-white font-bold text-sm">Apertura 3x piu' veloce</p>
        <p className="text-gray-500 text-xs">UI nativa, zero caricamento</p>
      </div>
    </div>
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl bg-[#F5A623]/20 flex items-center justify-center flex-shrink-0">
        <WifiOff className="w-4 h-4 text-[#F5A623]" />
      </div>
      <div>
        <p className="text-white font-bold text-sm">Funziona offline</p>
        <p className="text-gray-500 text-xs">Consulta le analisi anche senza connessione</p>
      </div>
    </div>
  </div>
);

const DownloadCTA = ({ source, label = 'Scarica l\'app ufficiale' }) => (
  <button
    onClick={() => handleDownloadClick(source)}
    className="w-full h-14 rounded-2xl bg-gradient-to-r from-[#F5A623] to-[#FF6B6B] text-black font-black text-base flex items-center justify-center gap-2 shadow-xl shadow-[#F5A623]/20 hover:shadow-2xl active:scale-95 transition-all"
    data-testid={`download-cta-${source}`}
  >
    <Download className="w-5 h-5" />
    {label}
  </button>
);


// ---------------------------------------------------------------------------
// Touchpoint 1 — Full-screen modal post-signup
// ---------------------------------------------------------------------------

// iter265 (2026-10-07) — Dismiss persistente lato device: se l'utente
// chiude il modal, NON riappare mai più su quel browser (localStorage,
// non sessionStorage). Prima si usava sessionStorage con delay 3s per
// la X — un dark pattern che si bloccava quando il component si
// ri-renderizzava (user auth refresh) e lasciava il popup inchiudibile.
const LS_MODAL_DISMISSED = 'rvincente_modal_dismissed_v1';

export const PostSignupDownloadModal = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (isRunningInApp() || hasUserDownloadedApp()) return;
    if (localStorage.getItem(LS_MODAL_DISMISSED) === '1') return;
    if (sessionStorage.getItem(SS_MODAL_SHOWN) === '1') return;
    setOpen(true);
    sessionStorage.setItem(SS_MODAL_SHOWN, '1');
  }, [user]);

  const closeModal = () => {
    localStorage.setItem(LS_MODAL_DISMISSED, '1');
    setOpen(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="app-download-modal">
      <div className="bg-[#0F0F0F] border border-gray-800 rounded-3xl w-full max-w-md p-6 relative shadow-2xl">
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors z-10"
          aria-label="Chiudi"
          data-testid="app-download-modal-close"
        >
          <X className="w-5 h-5 text-gray-300" />
        </button>
        <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-[#F5A623] to-[#FF6B6B] flex items-center justify-center mb-4 shadow-lg shadow-[#F5A623]/30">
          <img src="/icon-192.png" alt="RVincente" className="w-10 h-10 rounded-xl" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
        <h2 className="text-2xl font-black text-white leading-tight">Benvenuto! Ora scarica l'app</h2>
        <p className="text-gray-500 text-sm mt-1">Esperienza completa, piu' veloce, con notifiche</p>
        <FeatureBullets />
        <DownloadCTA source="modal" />
        <button
          onClick={closeModal}
          className="w-full h-10 text-xs text-gray-500 mt-2 hover:text-gray-300 transition-colors"
          data-testid="app-download-modal-continue-web"
        >
          Continua su web
        </button>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Touchpoint 2 — Sticky top banner
// ---------------------------------------------------------------------------

export const StickyDownloadBanner = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    // Gate: only authenticated users see the download push. Guests must
    // stay on the webapp to preserve Meta Pixel/CAPI attribution (fbclid is
    // lost when they hit Play Store / App Store).
    if (!user) return;
    if (isRunningInApp() || hasUserDownloadedApp()) return;
    if (sessionStorage.getItem(SS_BANNER_DISMISSED) === '1') return;
    const t = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(t);
  }, [user]);
  if (!user) return null;
  if (!visible) return null;
  return (
    <div className="sticky top-0 z-[90] bg-gradient-to-r from-[#F5A623] to-[#FF6B6B] text-black px-3 py-2 flex items-center gap-2 shadow-lg" data-testid="app-download-banner">
      <Download className="w-4 h-4 flex-shrink-0" />
      <p className="text-xs font-bold flex-1 leading-tight">Scarica l'app per notifiche e offline</p>
      <button onClick={() => handleDownloadClick('banner')} className="h-8 px-3 rounded-full bg-black text-white text-xs font-bold whitespace-nowrap" data-testid="app-download-banner-cta">Scarica</button>
      <button onClick={() => { sessionStorage.setItem(SS_BANNER_DISMISSED, '1'); setVisible(false); }} className="w-7 h-7 rounded-full bg-black/20 flex items-center justify-center flex-shrink-0" data-testid="app-download-banner-close">
        <X className="w-3 h-3 text-black" />
      </button>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Touchpoint 3 — Popup every 2 minutes (authenticated users only)
// ---------------------------------------------------------------------------

export const NavigationDownloadPopup = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // Gate: guests do NOT see this popup. We only push downloads after
    // Meta attribution has been captured server-side via CompleteRegistration.
    if (!user) return;
    if (isRunningInApp() || hasUserDownloadedApp()) return;
    // Fire every 2 minutes while the user is on the site.
    const tick = () => {
      if (hasUserDownloadedApp()) return;
      setOpen(true);
    };
    timerRef.current = setInterval(tick, 2 * 60 * 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [user]);

  if (!user) return null;
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-end justify-center p-4" data-testid="app-download-popup">
      <div className="bg-[#0F0F0F] border border-gray-800 rounded-3xl w-full max-w-md p-5 relative shadow-2xl animate-in slide-in-from-bottom">
        <button onClick={() => setOpen(false)} className="absolute top-3 right-3 w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center" data-testid="app-download-popup-close">
          <X className="w-4 h-4 text-gray-500" />
        </button>
        <h3 className="text-lg font-black text-white">Non perderti le analisi VIP</h3>
        <p className="text-gray-500 text-sm mt-1">Con l'app ricevi una notifica appena esce una nuova analisi</p>
        <div className="mt-4">
          <DownloadCTA source="popup" label="Scarica ora" />
        </div>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Touchpoint 4 — Bottom toast after 5 min of use
// ---------------------------------------------------------------------------

export const UsageTimerDownloadToast = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    if (isRunningInApp() || hasUserDownloadedApp()) return;
    if (sessionStorage.getItem(SS_TOAST_SHOWN) === '1') return;
    timerRef.current = setTimeout(() => {
      sessionStorage.setItem(SS_TOAST_SHOWN, '1');
      setVisible(true);
    }, 5 * 60 * 1000);
    return () => clearTimeout(timerRef.current);
  }, [user]);

  if (!user) return null;
  if (!visible) return null;
  return (
    <div className="fixed bottom-20 left-4 right-4 z-[85] max-w-md mx-auto" data-testid="app-download-toast">
      <div className="bg-[#F5A623] text-black rounded-2xl p-4 shadow-2xl flex items-center gap-3 animate-in slide-in-from-bottom">
        <Download className="w-6 h-6 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black leading-tight">Preferisci l'app?</p>
          <p className="text-xs leading-tight opacity-80">Esperienza ottimizzata e offline</p>
        </div>
        <button onClick={() => handleDownloadClick('toast')} className="h-9 px-4 rounded-full bg-black text-white text-xs font-bold" data-testid="app-download-toast-cta">Scarica</button>
        <button onClick={() => setVisible(false)} className="w-7 h-7 rounded-full bg-black/20 flex items-center justify-center flex-shrink-0" data-testid="app-download-toast-close">
          <X className="w-3 h-3 text-black" />
        </button>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Umbrella wrapper used by App.js
// ---------------------------------------------------------------------------

/**
 * Mount this ONCE inside AuthProvider. Each touchpoint internally decides
 * whether to render based on user state, running environment, and dismiss
 * history. Safe to mount even for anonymous users (they just see nothing
 * because `useAuth` returns no user → Modal auto-hides, Banner shows).
 */
const AppDownloadPrompts = () => {
  const { pathname } = useLocation();
  // Hide everything in TWA/standalone and on shared-selection links
  if (isRunningInApp() || pathname.startsWith('/s/') || pathname === '/conferma-email') return null;
  return (
    <>
      <PostSignupDownloadModal />
      <NavigationDownloadPopup />
      <UsageTimerDownloadToast />
      {/* StickyDownloadBanner is mounted separately in the layout header */}
    </>
  );
};

export default AppDownloadPrompts;
